import axios from 'axios';
import { FilterFlowDataProps } from '../../_components/atoms/FilterFlow';
import { ICohortDetails } from '../../_components/organisms/CreateNewModalCoreComponent';
import { ToggleValue } from '../custom-hooks/useReportsCohortItem';
import componentConstants from '../messages/componentConstants';
import { roundNumber } from './helper';
import properties from '../properties';

interface RecalculateResult {
  updatedCohortList: ICohortDetails[];
  finalMemberIds: string[];
}

const CONDITION_SYMBOL_MAP: Record<string, string> = {
  'is equal to': '=',
  'is not equal to': '≠',
  'is greater then equal to': '>=',
  'is less then equal to': '<=',
  is: '=',
  'is not': '≠',
};

export const buildSearchParams = (
  cohorts: ICohortDetails[],
  currentToggleList: ToggleValue[],
): string => {
  const parts: string[] = [];
  cohorts.forEach((cohort, index) => {
    const hasProperty = !!cohort.property;
    const hasMultiple =
      Array.isArray(cohort.multipleProperties) &&
      cohort.multipleProperties.length > 0;

    if (
      !cohort.category ||
      !cohort.condition ||
      (!hasProperty && !hasMultiple)
    ) {
      return;
    }

    const propertyPart = hasProperty
      ? cohort.property
      : cohort.multipleProperties!.join(';');

    parts.push(`${cohort.category}~${cohort.condition}~${propertyPart}^`);

    if (currentToggleList.length > 0 && index < currentToggleList.length) {
      parts.push(`${currentToggleList[index].toggleValue}^`);
    }
  });

  return parts.join('').replace(/\^+$/, '');
};

export const buildEmptyCohort = (id: number): ICohortDetails => ({
  id,
  category: '',
  condition: '',
  property: '',
  results: '',
  dropdownSelectionTypeIndex: 0,
  andOrCondition: 'AND',
  propertiesPageNumber: 0,
  totalPagesForProperties: 0,
});

export const buildConditionsDetails = (
  cohortDetailsList: ICohortDetails[],
): string[] => {
  const conditionsDetails: string[] = [];

  cohortDetailsList.forEach((detail, index) => {
    let conditionDetail = `${detail.category} ${detail.condition} `;

    if (detail.property) {
      conditionDetail += `${detail.property} `;
    } else if (detail.multipleProperties?.length) {
      conditionDetail += `${detail.multipleProperties.join(' | ')} `;
    }

    if (conditionDetail.trim()) {
      conditionsDetails.push(conditionDetail);
    }

    const isNotLast = index < cohortDetailsList.length - 1;
    if (isNotLast && detail.andOrCondition) {
      conditionsDetails.push(detail.andOrCondition);
    }
  });

  return conditionsDetails;
};

export const buildFilterLabel = (
  category: string,
  condition: string,
  property: string,
  multipleProperties: string[],
  selectionType: string,
): string => {
  if (!category || !condition) {
    return '';
  }

  const symbol = CONDITION_SYMBOL_MAP[condition] ?? condition;

  if (selectionType === 'multiple' && multipleProperties?.length > 0) {
    return `${category} ${symbol} ${multipleProperties.join(', ')}`;
  }

  if (property) {
    return `${category} ${symbol} ${property}`;
  }

  return '';
};

export const buildFilterFlowData = (
  cohortDetailsList: ICohortDetails[],
  cohortSelectionsTypes: string[],
  totalCount: number,
): FilterFlowDataProps[] => {
  const messages = componentConstants.ORGANISMS.COHORT_DETAILS;

  const result: FilterFlowDataProps[] = [
    {
      label: messages.TOTAL_PATIENTS,
      count: totalCount,
      percent: '100%',
      type: 'TOTAL',
    },
  ];

  cohortDetailsList.forEach((cohort) => {
    const selectionType =
      cohortSelectionsTypes[cohort.dropdownSelectionTypeIndex];

    const label = buildFilterLabel(
      cohort.category,
      cohort.condition,
      cohort.property,
      cohort.multipleProperties ?? [],
      selectionType,
    );

    if (label && cohort.results) {
      const count = parseInt(cohort.results);
      const percent = cohort.percentage ? `${cohort.percentage}%` : '0%';

      result.push({
        label,
        count,
        percent,
        type: 'FILTER',
      });
    }
  });

  return result;
};

export const recalculateEachCohort = async (
  cohortDetailsList: ICohortDetails[],
  totalCount: number,
  startDate?: string,
  endDate?: string,
  employerGroup?: string[],
  type?: string,
  member_unique_ids: string[] = [],
  max_claim_date?: string,
): Promise<RecalculateResult> => {
  const updatedCohortList: ICohortDetails[] = [];
  let finalMemberIds: string[] = [];
  let stopProcessing = false;

  for (let i = 0; i < cohortDetailsList.length; i++) {
    const cohort = cohortDetailsList[i];

    const hasProperty =
      cohort.property !== null &&
      cohort.property !== undefined &&
      cohort.property !== '';

    const hasMultiple =
      Array.isArray(cohort.multipleProperties) &&
      cohort.multipleProperties.length > 0;

    const isValid =
      !!cohort.category && !!cohort.condition && (hasProperty || hasMultiple);

    if (!isValid || stopProcessing) {
      stopProcessing = true;
      updatedCohortList.push(cohort);
      continue;
    }

    const cumulativeCohorts = cohortDetailsList.slice(0, i + 1);
    const toggleList: ToggleValue[] = cohortDetailsList
      .slice(0, i)
      .map((item) => ({
        toggleValue: item.andOrCondition?.toLowerCase() === 'or' ? 'or' : 'and',
      }));

    const searchParams = buildSearchParams(cumulativeCohorts, toggleList);
    try {
      const response = await axios.post(
        properties.aiqBaseUrl +
          properties.summaryBaseUrl +
          properties.searchCohortMembers,
        {
          startDate,
          endDate,
          eg_nid: employerGroup,
          searchParams,
          type: type ?? 'data',
          member_unique_id_list: member_unique_ids,
          max_claim_date,
        },
      );
      const data = response.data?.data ?? {};
      const count = data.member_ids_count ?? 0;
      finalMemberIds = data.member_ids ?? finalMemberIds;

      const percentage =
        totalCount > 0 ? roundNumber((count / totalCount) * 100, 2) : 0;

      updatedCohortList.push({
        ...cohort,
        results: String(count),
        percentage: String(percentage),
      });
    } catch (error) {
      stopProcessing = true;
      updatedCohortList.push(cohort);
    }
  }
  return { updatedCohortList, finalMemberIds };
};
